import React, { useEffect, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import BrandLogo from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";
import { Asterisk, Check, X, Loader2, Sun, AlertTriangle } from "lucide-react";

const CAN = [
  "Read the bids, leads, clients, documents, libraries and benchmarks you can see",
  "Build and edit estimates: line items, assemblies, crews, calculators, terms and proposal sections",
  "Add documents to bids, and create leads, clients, contacts and notes",
  "Move bids between active stages and submit a bid for BDM sign-off",
  "Prepare email drafts that wait for you to review and send yourself",
];
const NEVER = [
  "Send or schedule emails on its own",
  "Send proposals or mark a bid as sent",
  "Approve, reject or override a sign-off",
  "Record a bid as won, lost, canceled or no-bid",
  "Delete bids, leads, clients or documents",
  "Change Settings, users, roles or the libraries",
];

function Rules({ title, items, ok, testid }) {
  const Icon = ok ? Check : X;
  return (
    <div data-testid={testid}>
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">{title}</div>
      <ul className="space-y-1">
        {items.map((t) => (
          <li key={t} className="flex gap-2 text-sm text-slate-700">
            <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${ok ? "text-emerald-600" : "text-rose-500"}`} />{t}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-lg bg-white border border-slate-200 rounded-2xl shadow-sm p-7" data-testid="claude-connect-page">
        <div className="flex items-center gap-3 mb-6">
          <BrandLogo size={32} />
          <span className="text-slate-300">+</span>
          <span className="w-8 h-8 rounded-lg grid place-items-center bg-[#D97757]/10 text-[#D97757]"><Asterisk className="w-5 h-5" /></span>
        </div>
        {children}
      </div>
    </div>
  );
}

export default function ClaudeConnect() {
  const { user, loading } = useAuth();
  const [params] = useSearchParams();
  const tx = params.get("tx") || "";
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState("");

  useEffect(() => {
    if (!user || !tx) return;
    api.get(`/oauth/transaction/${tx}`).then((r) => setInfo(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "This connection request isn't valid."));
  }, [user, tx]);

  if (loading) return <Shell><Sun className="w-6 h-6 animate-spin text-[#003E87] mx-auto" /></Shell>;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(`/claude/connect?tx=${tx}`)}`} replace />;

  const decide = async (approve) => {
    setBusy(approve ? "allow" : "deny");
    try {
      const r = await api.post("/oauth/consent", { tx, approve });
      window.location.href = r.data.redirect_to;
    } catch (e) { setErr(e.response?.data?.detail || "Could not finish connecting."); setBusy(""); }
  };

  if (err || !tx) {
    return (
      <Shell>
        <div data-testid="claude-connect-error" className="flex gap-2 text-sm text-slate-700"><AlertTriangle className="w-5 h-5 text-amber-500 shrink-0" />{err || "This connection request isn't valid. Go back to Claude and click Connect again."}</div>
      </Shell>
    );
  }
  if (!info) return <Shell><Loader2 className="w-6 h-6 animate-spin text-[#003E87] mx-auto" /></Shell>;

  return (
    <Shell>
      <h1 className="font-display text-2xl font-bold tracking-tight text-black">Connect {info.app} to GridForge</h1>
      <p className="text-sm text-slate-500 mt-1" data-testid="claude-connect-who">
        Signed in as <b className="text-slate-800">{info.user.name || info.user.email}</b> ({info.user.email}). {info.app} will work with your access. It can never do more than you can.
      </p>
      <div className="mt-3 text-xs text-slate-500" data-testid="claude-connect-redirect">You'll return to <b className="text-slate-700">{info.redirect_host}</b>{info.loopback ? ", a program on this computer. Only continue if you just started Claude Code here." : "."}</div>
      {!info.enabled ? (
        <div data-testid="claude-connect-off" className="mt-5 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">The Claude connector is turned off for your company. Ask an owner or admin to turn it on in Settings → Claude connector.</div>
      ) : (
        <div className="mt-5 space-y-4">
          <Rules title={`${info.app} will be able to`} items={CAN} ok testid="claude-connect-can" />
          <Rules title="It will never" items={NEVER} testid="claude-connect-never" />
          <p className="text-xs text-slate-500">Changes made through {info.app} show under your own name; only you see which ones came through {info.app}, and you can undo them from the bid. You can disconnect any time in Settings → Claude connector.</p>
        </div>
      )}
      <div className="mt-6 flex justify-end gap-2">
        <Button variant="ghost" data-testid="claude-connect-deny" disabled={!!busy} onClick={() => decide(false)}>Cancel</Button>
        {info.enabled && (
          <Button data-testid="claude-connect-allow" disabled={!!busy} onClick={() => decide(true)} className="bg-[#F4C400] hover:bg-[#D9AE00] text-black font-semibold">
            {busy === "allow" && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}Allow
          </Button>
        )}
      </div>
    </Shell>
  );
}
